import React, { useEffect, useState } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { updateServiceWorker } from '../../lib/pwa'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface UpdateNotificationProps {
  position?: 'top' | 'bottom'
  autoUpdate?: boolean
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  position = 'top',
  autoUpdate = false
}) => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    const handleUpdateAvailable = () => {
      console.log('Update notification: Update available')
      setIsUpdateAvailable(true)

      // Auto-update if enabled
      if (autoUpdate) {
        setTimeout(() => {
          handleUpdate()
        }, 3000)
      }
    }

    window.addEventListener('pwa-update-available', handleUpdateAvailable)

    return () => {
      window.removeEventListener('pwa-update-available', handleUpdateAvailable)
    }
  }, [autoUpdate])

  const handleUpdate = async () => {
    setIsUpdating(true)

    try {
      await updateServiceWorker()
      // Page will reload automatically
    } catch (error) {
      console.error('Update failed:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          type="error"
          title="Update Failed"
          message="Please refresh the page manually"
          visible={t.visible}
        />
      ))
      setIsUpdating(false)
    }
  }

  const handleDismiss = () => {
    setIsUpdateAvailable(false)
  }

  if (!isUpdateAvailable) {
    return null
  }

  return (
    <div
      className={`fixed left-0 right-0 z-[99999] animate-slide-down ${
        position === 'top' ? 'top-[150px]' : 'bottom-20'
      }`}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="bg-card border-2 border-primary rounded-lg shadow-2xl p-6">
          <div className="flex items-start space-x-4">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                <RefreshCw className="text-primary" size={24} />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-foreground mb-2">
                Update Available
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                A new version of SECRET is available with improvements and bug fixes.
                Click below to update now.
              </p>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold text-base px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-sm"
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      <span>Update Now</span>
                    </>
                  )}
                </button>

                {!autoUpdate && (
                  <button
                    onClick={handleDismiss}
                    className="px-4 py-3 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Dismiss update notification"
                  >
                    <X size={24} />
                  </button>
                )}
              </div>

              {isUpdating && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Installing update... The page will reload automatically.
                  </p>
                </div>
              )}

              {autoUpdate && !isUpdating && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Updating automatically in 3 seconds...
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpdateNotification
